import type { Move } from "../../../royalur/model/Move.js";
import { rand } from "../../../util/random.js";
import type { Game } from "../../../royalur/Game.js";
import { Agent } from "../../../royalur/agent/Agent.js";
import { LikelihoodAgent } from "../../../royalur/agent/LikelihoodAgent.js";
import { PiecesAdvancedUtilityFn } from "../../../royalur/agent/utility/PiecesAdvancedUtilityFn.js";
import { SimpleEngine } from "../../../royalur/engine/simple/SimpleEngine.js";
import type { Engine } from "../../../royalur/engine/Engine.js";


/**
 * The strategy of the easy agent on RoyalUr.net. It intentionally
 * plays badly, but hopefully in a way that is still fun to beat.
 */
export class MediumAgent extends Agent {

    public static readonly LIKELIHOOD_THRESHOLD = 0.05;

    private static readonly RANDOM_AMOUNT = 2.0;

    private readonly delegate: LikelihoodAgent;

    constructor(rules: Engine) {
        super();

        if (!(rules instanceof SimpleEngine))
            throw new Error("Only standard rule sets are supported for the medium agent");

        const utilityFn = new PiecesAdvancedUtilityFn(rules);
        this.delegate = new LikelihoodAgent(rules, utilityFn, MediumAgent.LIKELIHOOD_THRESHOLD);
    }

    private addRandom(): number {
        return rand(0.0, MediumAgent.RANDOM_AMOUNT);
    }

    orderMoves(moves: Move[]) {
        // Less advanced pieces have lower path indices.
        const computePathIndex = (move: Move) => {
            return move.isIntroduction() ? -1 : move.getSourcePiece().getPathIndex();
        };

        // Sort so that least advanced pieces come first.
        moves.sort((a: Move, b: Move) => {
            return computePathIndex(a) - computePathIndex(b);
        });
    }

    override decideMove(game: Game, moves: Move[]): Move {
        this.orderMoves(moves);
        const moveUtilities = this.delegate.scoreMoves(game, moves);

        let bestMove: Move = moves[0];
        let bestMoveUtility: number = moveUtilities[0] + this.addRandom();

        for (let index = 1; index < moves.length; ++index) {
            const move = moves[index];
            const realUtility = moveUtilities[index];
            const effectiveUtility = realUtility + index + this.addRandom();
            if (effectiveUtility > bestMoveUtility) {
                bestMove = move;
                bestMoveUtility = effectiveUtility;
            }
        }
        return bestMove;
    }
}
